import type { Consent } from 'ProdantixSdk/core/types';

export const CATEGORIES = ['analytics', 'marketing', 'functional'] as const;

export type Category = (typeof CATEGORIES)[number];

export type BannerTexts = {
  acceptAll: string;
  analytics: string;
  body: string;
  functional: string;
  marketing: string;
  necessary: string;
  policyLabel: string;
  rejectAll: string;
  save: string;
  settings: string;
  title: string;
};

export type BannerStyle = { background: string; button: string; buttonText: string; radius: number; text: string };

/** The banner as the edge serves it, in the page's language. */
export type ServedBanner = {
  categories: Category[];
  language: string;
  layout: 'bar_bottom' | 'bar_top' | 'box';
  policyUrl: string;
  poweredBy: boolean;
  poweredByText: string;
  style: BannerStyle;
  texts: BannerTexts;
  version: number;
};

export type ConsentAnswer = { anonymousId: string; banner: ServedBanner | null; region: string; rule: 'notice' | 'opt_in' };

/** What the page's banner has decided, for any Prodantix script that loads
 *  after it: the rule, the day's cookieless id, and the answer once given. */
export type ConsentState = { anonymousId: string; consent: Consent | null; rule: 'notice' | 'opt_in' };

export const READY_EVENT = 'prodantix:consent-ready';

export const ANSWER_EVENT = 'prodantix:consent';

export const STATE_KEY = '__prodantixConsent';

export type ConsentWindow = { [STATE_KEY]?: ConsentState };

/** Whether the visitor is asked: never answered, or the banner now asks a
 *  category their answer never covered. */
export const needsAsking = (stored: Consent | null, banner: ServedBanner): boolean => {
  if (stored === null || stored.version === undefined) return true;
  const answered = stored.categories ?? [];
  return banner.categories.some(category => !answered.includes(category));
};

/** The answer to every category the banner asks, the rest refused. */
export const answerOf = (banner: ServedBanner, granted: ReadonlySet<Category>): Consent => ({
  analytics: banner.categories.includes('analytics') && granted.has('analytics'),
  categories: [...banner.categories],
  functional: banner.categories.includes('functional') && granted.has('functional'),
  marketing: banner.categories.includes('marketing') && granted.has('marketing'),
  version: banner.version,
});

/** The categories an answer grants. */
export const grantedBy = (consent: Consent | null): Set<Category> => new Set(CATEGORIES.filter(category => consent?.[category] === true));
